import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { getAdminUserTasksApi, getMyTasksApi } from "../../../app/api";
import { createPortal } from "react-dom";
import TaskSubtasks from "../../../components/Tasks/TaskSubtasks";
import TaskDrawer from "../../../components/Tasks/TaskDrawer";

const IMAGE_BASE_URL = "https://uptask.visiontrek.io";

const STATUSES = [
  { key: "TODO", label: "To Do" },
  { key: "IN_PROGRESS", label: "In Progress" },
  { key: "PENDING", label: "Pending" },
  { key: "DONE", label: "Completed" },
];

export default function UserTaskBoard({ userId }) {
  const [activeFilter, setActiveFilter] = useState("ALL");
  const { id: paramId } = useParams();
  const id = userId || paramId;
  const navigate = useNavigate();

  const [tasks, setTasks] = useState([]);
  const [selectedTask, setSelectedTask] = useState(null);

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const [currentPage, setCurrentPage] = useState(1);
  const tasksPerPage = 8;

  useEffect(() => {
    if (id) loadTasks();
  }, [id]);

  const loadTasks = async () => {
    try {
      const loggedUser = JSON.parse(localStorage.getItem("user"));
      const isOwnProfile = Number(loggedUser?.id) === Number(id);

      const res = isOwnProfile 
        ? await getMyTasksApi()
        : await getAdminUserTasksApi(id);

      if (res.success) {
        setTasks(res.data);
      } else {
        toast.error("Failed to load tasks");
      }
    } catch {
      toast.error("Something went wrong");
    }
  };

  const handleStatusChange = (updatedTask) => {
    setTasks((prev) =>
      prev.map((t) => (t.id === updatedTask.id ? updatedTask : t))
    );
    setSelectedTask(updatedTask);
  };

  const getCreatorName = (task) => {
    if (typeof task.creator === "string") return task.creator;
    if (typeof task.creator === "object") return task.creator?.name || "";
    return "";
  };

  const getCreatorImage = (task) => {
    return task.creator_profile_pic || task.creator?.profile_pic || null;
  };



  // const filteredTasks = tasks.filter((task) => {
  //   if (!startDate && !endDate) return true;

  //   const createdDate = task.created_at
  //     ? new Date(task.created_at.split(" ")[0])
  //     : null;

  //   const dueDate = task.due_date
  //     ? new Date(task.due_date.split(" ")[0])
  //     : null;

  //   const start = startDate ? new Date(startDate) : null;
  //   const end = endDate ? new Date(endDate) : null;

  //   // ✅ FILTER BY RANGE
  //   if (start && createdDate && createdDate < start) return false;
  //   if (end && createdDate && createdDate > end) return false;

  //   return true;
  // });

  const filteredTasks = tasks.filter((task) => {
    // ✅ STATUS FILTER
    if (activeFilter === "DONE" && task.status !== "DONE") return false;
    if (activeFilter === "HIGH" && task.priority !== "HIGH") return false;

    // ✅ DATE FILTER
    if (!startDate && !endDate) return true;

    const createdDate = task.created_at
      ? new Date(task.created_at.split(" ")[0])
      : null;

    const start = startDate ? new Date(startDate) : null;
    const end = endDate ? new Date(endDate) : null;

    if (start && createdDate && createdDate < start) return false;
    if (end && createdDate && createdDate > end) return false;

    return true;
  });


  const sortedTasks = [...filteredTasks].sort((a, b) => {
    const dateA = a.due_date ? new Date(a.due_date.split(" ")[0]) : new Date(0);
    const dateB = b.due_date ? new Date(b.due_date.split(" ")[0]) : new Date(0);
    return dateB - dateA;
  });

  const indexOfLastTask = currentPage * tasksPerPage;
  const indexOfFirstTask = indexOfLastTask - tasksPerPage;

  const currentTasks = sortedTasks.slice(indexOfFirstTask, indexOfLastTask);

  const totalPages = Math.ceil(sortedTasks.length / tasksPerPage);

  const STATUS_LABELS = {
    TODO: "To Do",
    IN_PROGRESS: "In Progress",
    HOLD: "Hold",
    REVIEW: "Review",
    DONE: "Done",
  };

  return (

    <div className="p-0 rounded-lg">

      <div className="max-w-[1600px] mx-auto      rounded-2xl  mt-4
         
          shadow-2xl">
        <div className="flex justify-end">
        </div>

        {/* ===== FILTER BAR ===== */}
        <div className="mt-[-14px]   bg-gradient-to-br from-[#2d1c6a] via-[#24155a] to-[#12082f] bg-white/5 backdrop-blur-md p-4 rounded-xl shadow-md border border-white/10 mb-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div className="flex gap-3 flex-wrap">
              <button
                onClick={() => setActiveFilter("ALL")}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition ${activeFilter === "ALL"
                  ? "bg-indigo-600 text-white shadow-lg shadow-indigo-500/30"
                  : "bg-white/10 text-white/70 hover:bg-white/20"
                  }`}
              >
                All
              </button>

              <button
                onClick={() => setActiveFilter("DONE")}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition ${activeFilter === "DONE"
                  ? "bg-green-600 text-white shadow-lg shadow-green-500/30"
                  : "bg-white/10 text-white/70 hover:bg-white/20"
                  }`}
              >
                Completed
              </button>

              <button
                onClick={() => setActiveFilter("HIGH")}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition ${activeFilter === "HIGH"
                  ? "bg-red-600 text-white shadow-lg shadow-red-500/30"
                  : "bg-white/10 text-white/70 hover:bg-white/20"
                  }`}
              >
                High Priority
              </button>
            </div>

            <div className="flex flex-wrap gap-3 items-end">
              <div className="flex flex-col">
                <label className="text-xs text-white/50 mb-1 ml-1">From Date</label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="bg-white/10 border border-white/20 px-3 py-2 rounded-lg text-sm outline-none text-white focus:border-indigo-500 transition"
                />
              </div>

              <div className="flex flex-col">
                <label className="text-xs text-white/50 mb-1 ml-1">To Date</label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="bg-white/10 border border-white/20 px-3 py-2 rounded-lg text-sm outline-none text-white focus:border-indigo-500 transition"
                />
              </div>

              <button
                onClick={loadTasks}
                className="bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-2 rounded-lg text-sm font-medium shadow-lg shadow-indigo-500/30 transition"
              >
                Apply
              </button>

              <button
                onClick={() => {
                  setStartDate("");
                  setEndDate("");
                  loadTasks();
                }}
                className="bg-white/10 text-white/70 hover:bg-white/20 px-4 py-2 rounded-lg text-sm transition"
              >
                Clear
              </button>
            </div>
          </div>
        </div>

        {/* ===== TASK GRID ===== */}
        {/* <div className="  bg-gradient-to-br from-[#120932] via-[#1a0f42] to-[#12082f] bg-white/5 backdrop-blur-xl md:p-8 rounded-[30px] border border-white/10 shadow-2xl"> */}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
            {currentTasks.length === 0 ? (
              <div className="col-span-full flex flex-col items-center justify-center text-center py-20">
                <div className="text-5xl mb-4">📭</div>
                <h3 className="text-white text-lg font-semibold">No Tasks Available</h3>
                <p className="text-white/40 text-sm mt-1">Try changing filters or date range</p>
              </div>
            ) : (
              currentTasks.map((task) => {
                const creatorName = getCreatorName(task);
                const creatorImage = getCreatorImage(task);

                // DEADLINE STATUS
                const todayDate = new Date().toISOString().split("T")[0];
                const dueDate = task.due_date?.split(" ")[0];
                let deadlineStatus = null;
                if (dueDate && task.status !== "DONE" && task.status !== "HOLD") {
                  if (dueDate < todayDate) {
                    deadlineStatus = "OVERDUE";
                  } else if (dueDate === todayDate) {
                    deadlineStatus = "TODAY";
                  }
                }

                return (
                  <div
                    key={task.id}
                    onClick={() => setSelectedTask(task)}
                    className="
                      group relative overflow-hidden rounded-[24px] p-5
                      bg-white/5 backdrop-blur-md border border-white/10
                      shadow-[0_8px_32px_0_rgba(0,0,0,0.3)]
                      hover:bg-white/10 hover:border-white/20 hover:scale-[1.02]
                      transition-all duration-300 cursor-pointer
                    "
                  >
                    {/* Glass Shine */}
                    <div className="absolute -top-24 -right-24 w-48 h-48 bg-white/5 rounded-full blur-3xl group-hover:bg-indigo-500/10 transition-colors duration-500"></div>

                    {/* Header: Priority & Deadline */}
                    <div className="flex justify-between items-start mb-4">
                      <span
                        className={`text-[10px] px-2.5 py-1 rounded-full font-bold tracking-wider
                          ${task.priority === "HIGH"
                            ? "bg-red-500/20 text-red-400 border border-red-500/20"
                            : task.priority === "MEDIUM"
                              ? "bg-yellow-500/20 text-yellow-400 border border-yellow-500/20"
                              : "bg-green-500/20 text-green-400 border border-green-500/20"
                          }
                        `}
                      >
                        {task.priority || "LOW"}
                      </span>

                      {deadlineStatus && (
                        <span
                          className={`text-[10px] px-2.5 py-1 rounded-full font-bold animate-pulse
                            ${deadlineStatus === "OVERDUE" ? "bg-red-600 text-white" : "bg-orange-500 text-white"}
                          `}
                        >
                          {deadlineStatus}
                        </span>
                      )}
                    </div>

                    <h4 className="text-white font-bold text-[15px] mb-2 leading-tight group-hover:text-indigo-300 transition-colors">
                      {task.title}
                    </h4>

                    <p className="text-white/50 text-xs mb-4 line-clamp-2 leading-relaxed">
                      {task.description}
                    </p>

                    {/* Progress Bar */}
                    <div className="mb-4">
                      <div className="flex justify-between text-[10px] text-white/40 mb-1.5">
                        <span>Progress</span>
                        <span className="text-white/70 font-medium">{task.completion_percent || 0}%</span>
                      </div>
                      <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full transition-all duration-500"
                          style={{ width: `${task.completion_percent || 0}%` }}
                        ></div>
                      </div>
                    </div>

                    {/* Dates */}
                    <div className="flex justify-between p-3 bg-white/5 rounded-xl border border-white/5 mb-4 text-[10px]">
                      <div>
                        <p className="text-white/30 mb-0.5">Created</p>
                        <p className="text-white/80 font-medium">{task.created_at?.split(" ")[0] || "--"}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-white/30 mb-0.5">Due Date</p>
                        <p className={`font-medium ${deadlineStatus === 'OVERDUE' ? 'text-red-400' : 'text-white/80'}`}>
                          {task.due_date?.split(" ")[0] || "--"}
                        </p>
                      </div>
                    </div>

                    {/* Footer */}
                    <div className="flex items-center justify-between pt-3 border-t border-white/10">
                      <div className="flex items-center gap-2">
                        {creatorImage ? (
                          <img
                            src={`${IMAGE_BASE_URL}${creatorImage}`}
                            className="w-6 h-6 rounded-full object-cover border border-white/20"
                            alt={creatorName}
                          />
                        ) : (
                          <div className="w-6 h-6 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white text-[10px] font-bold">
                            {creatorName?.charAt(0)}
                          </div>
                        )}
                        <span className="text-[11px] text-white/60 font-medium">{creatorName}</span>
                      </div>

                      <span className={`text-[10px] px-2.5 py-1 rounded-lg font-semibold
                        ${task.status === "DONE"
                          ? "bg-green-500/20 text-green-400 border border-green-500/20"
                          : task.status === "IN_PROGRESS"
                            ? "bg-indigo-500/20 text-indigo-400 border border-indigo-500/20"
                          : task.status === "HOLD"
                            ? "bg-red-500/20 text-red-400 border border-red-500/20"
                            : "bg-white/10 text-white/60 border border-white/10"
                        }
                      `}>
                        {STATUS_LABELS[task.status] || task.status}
                      </span>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        {/* </div> */}








      </div>


      {/* ===== PAGINATION ===== */}

      {totalPages > 1 && (

        <div className="flex justify-center mt-8 gap-2 flex-wrap">

          {Array.from({ length: totalPages }, (_, i) => (

            <button
              key={i}
              onClick={() => setCurrentPage(i + 1)}
              className={`px-4 py-2 rounded-md text-sm shadow
${currentPage === i + 1
                  ? "bg-indigo-600 text-white"
                  : "bg-gray-200 hover:bg-gray-300 text-gray-700"}
`}
            >
              {i + 1}
            </button>

          ))}

        </div>

      )}

      {selectedTask && (
      <TaskDrawer
        open={!!selectedTask}
        task={selectedTask}
        onClose={() => setSelectedTask(null)}
        onStatusChange={handleStatusChange}
      />
      )}

    </div>

  );
}

